/**
 * Audio utilities split out of audioEngine.js during the iter-30 refactor.
 *
 * Both functions here are pure (no `this`, no module-level state). Kept
 * separate so the engine class itself stays focused on graph/mutation
 * concerns and so test files can import these without spinning up an
 * AudioEngine.
 */

/**
 * Build a stereo reverb impulse response procedurally — a decaying white-noise
 * tail. Used by the per-channel reverb ConvolverNode. Real-world reverbs sample
 * actual rooms, but a procedural IR is "good enough" for the in-browser DAW
 * use case and costs nothing in bundle size.
 *
 * @param {AudioContext} ctx - The audio context to allocate the buffer on.
 * @param {number} seconds - Tail length in seconds (default 2.4).
 * @param {number} decay - Exponential decay shape (higher = quicker fade).
 */
export function makeReverbIR(ctx, seconds = 2.4, decay = 2.5) {
  const rate = ctx.sampleRate;
  const length = Math.max(1, Math.floor(rate * seconds));
  const ir = ctx.createBuffer(2, length, rate);
  for (let c = 0; c < 2; c++) {
    const data = ir.getChannelData(c);
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
    }
  }
  return ir;
}

/**
 * Encode an AudioBuffer as a standard PCM 16-bit WAV Blob.
 *
 * Bytes layout: RIFF / WAVE header (44 B) + interleaved s16le samples.
 * Stereo is interleaved L/R. Sample rate + channel count come from `buffer`.
 *
 * Performance note: this is O(buffer.length * numCh). For a 5-minute stereo
 * 48 kHz mix that's ~30 M iterations — runs in ~50 ms on a modern laptop,
 * which is well below the user-perceptible threshold for a one-click export.
 *
 * @param {AudioBuffer} buffer - The source buffer (any sample rate / channel count).
 * @returns {Blob} A `audio/wav` blob ready for download or upload.
 */
export function audioBufferToWavBlob(buffer) {
  const numCh = buffer.numberOfChannels;
  const len = buffer.length * numCh * 2 + 44;
  const view = new DataView(new ArrayBuffer(len));
  const channels = [];
  for (let i = 0; i < numCh; i++) channels.push(buffer.getChannelData(i));

  let offset = 0;
  function writeStr(s) { for (let i = 0; i < s.length; i++) view.setUint8(offset++, s.charCodeAt(i)); }
  function writeU32(v) { view.setUint32(offset, v, true); offset += 4; }
  function writeU16(v) { view.setUint16(offset, v, true); offset += 2; }

  writeStr("RIFF");
  writeU32(len - 8);
  writeStr("WAVE");
  writeStr("fmt ");
  writeU32(16);
  writeU16(1);
  writeU16(numCh);
  writeU32(buffer.sampleRate);
  writeU32(buffer.sampleRate * numCh * 2);
  writeU16(numCh * 2);
  writeU16(16);
  writeStr("data");
  writeU32(buffer.length * numCh * 2);

  for (let i = 0; i < buffer.length; i++) {
    for (let c = 0; c < numCh; c++) {
      const sample = Math.max(-1, Math.min(1, channels[c][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }
  return new Blob([view.buffer], { type: "audio/wav" });
}
